import React, { useState, useRef } from "react";
import NewTrackCategoryModal from "./NewTrackCategoryModal";
import TrackCategoryTable from "../tables/TrackCategoryTable";
import { successMsg } from "../SuccessHandler";
import ErrorCodes from "../../constants/ErrorCodes";
import { useUpdateFunctions, useInsertFunctions } from "../../functions";
import ConfirmDialog from "../utilities/ConfirmDialog";

const TrackCategoryModal = ({
  trackCategory,
  setTrackCategory,
  data,
  getTrackCategory,
  sessionNoLongerExists,
  setSessionNoLongerExists,
}) => {
  const divRef = useRef(null);
  const closeButtonRef = useRef();
  const tableRef = useRef(null);
  const [shouldFocusLast, setShouldFocusLast] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [newCategory, setNewCategory] = useState({
    CategoryOrder: 0,
    Description: "",
    LastModification: "",
  });
  const { updateTrackCategory } = useUpdateFunctions();
  const { insertTrackCategory } = useInsertFunctions();

  // Update Track Categories Function
  const updateTrackCategoriesList = (categories, close) => {
    if (hasChanges) {
      let hasError = false;

      categories.forEach((row) => {
        [row].forEach((cell) => {
          if (!cell.Description.trim()) {
            hasError = true;
          }
          if (cell.CategoryOrder < 0) {
            hasError = true;
          }
        });
      });

      if (hasError && close) {
        closeButtonRef.current.click();
        setTrackCategory([]);
      } else if (!hasError) {
        let msgCancel;
        if (close) {
          msgCancel = "Exit anyway";
        } else {
          msgCancel = "Cancel";
        }
        ConfirmDialog(`Changes will be saved!`, msgCancel).then(
          async (result) => {
            if (result.isConfirmed) {
              let InParams = [];
              categories.forEach((element) => {
                if (element.Change) {
                  InParams.push({
                    ...element,
                    CategoryOrder: parseInt(element.CategoryOrder),
                  });
                }
              });

              if (InParams.length === 0) {
                InParams = {};
              }

              const res = await updateTrackCategory(
                {
                  Categories: InParams,
                },
                data.LogonSessionID
              );

              if (res?.error) {
                if (
                  res.Result === ErrorCodes.InvalidSession ||
                  res.Result === ErrorCodes.SessionNoLongerExists
                ) {
                  setSessionNoLongerExists(true);
                }
                return;
              } else {
                successMsg("Save Categories");
                setHasChanges(false);
                if (close) {
                  closeButtonRef.current.click();
                  setTrackCategory([]);
                  setHasChanges(false);
                }
              }
            } else {
              if (close) {
                closeButtonRef.current.click();
                setTrackCategory([]);
                setHasChanges(false);
              }
            }
          }
        );
      }
    }
  };

  //Insert Track Category Function
  const insertCategory = async (newCategory) => {
    const _errors = {};
    if (!newCategory.Description)
      _errors.description = "Description is required";
    if (newCategory.CategoryOrder < 0)
      _errors.categoryOrder = "Order cannot be negative";

    setNewCategory({ errors: _errors });
    if (Object.keys(_errors).length === 0) {
      const res = await insertTrackCategory(
        {
          ...newCategory,
        },
        data.LogonSessionID
      );

      if (res?.error) {
        if (
          res.Result === ErrorCodes.InvalidSession ||
          res.Result === ErrorCodes.SessionNoLongerExists
        ) {
          setSessionNoLongerExists(true);
        }
        return;
      } else {
        successMsg("Save Category");
        getTrackCategory();
        setShouldFocusLast(true);
      }

      setNewCategory({
        CategoryOrder: 0,
        Description: "",
        LastModification: "",
      });
      setShouldFocusLast(false);
    }
  };

  // This function is executed when closing the modal from the close button or from x
  const closeModal = () => {
    if (hasChanges) {
      updateTrackCategoriesList(trackCategory, true);
    } else {
      closeButtonRef.current.click();
      const div = divRef.current;
      if (div) {
        div.scrollTop = 0;
      }

      const rows = tableRef.current.getElementsByTagName("tr");
      const lastRow = rows[rows.length - 1];
      lastRow.style.backgroundColor = null;

      setTrackCategory([]);
    }
  };

  const refreshCategories = () => {
    getTrackCategory();
  };

  return (
    <>
      <div
        className="modal fade"
        id="trackCategoryModal"
        role="dialog"
        aria-hidden="false"
        data-keyboard="false"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-sm">
          <div className="modal-content">
            <div className="modal-header headerModals">
              <h5 className="modal-title" id="trackCategoryModal">
                Track Category
              </h5>
              <span
                data-dismiss="modal"
                ref={closeButtonRef}
                style={{ cursor: "inherit" }}
              ></span>
              <button type="button" className="close" onClick={closeModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {trackCategory ? (
                <TrackCategoryTable
                  trackCategory={trackCategory}
                  setTrackCategory={setTrackCategory}
                  data={data}
                  divRef={divRef}
                  tableRef={tableRef}
                  shouldFocusLast={shouldFocusLast}
                  setHasChanges={setHasChanges}
                  setShouldFocusLast={setShouldFocusLast}
                  sessionNoLongerExist={sessionNoLongerExists}
                  setSessionNoLongerExists={setSessionNoLongerExists}
                />
              ) : (
                <div style={{ textAlign: "center", color: "#808080" }}>
                  <span className="parpadea">Loading...</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="col-sm-6">
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={() => refreshCategories()}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-toggle="modal"
                  data-target="#newTrackCategoryModal"
                >
                  New Category
                </button>
              </div>
              <div
                className="col-sm-6"
                style={{ textAlign: "right", paddingRight: "0px" }}
              >
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() =>
                    updateTrackCategoriesList(trackCategory, false)
                  }
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <NewTrackCategoryModal
        newCategory={newCategory}
        setNewCategory={setNewCategory}
        insertCategory={insertCategory}
      />
    </>
  );
};

export default TrackCategoryModal;
